import { useState } from 'react';
import { Link } from 'react-router-dom';

type Lottery = 'megamillions' | 'powerball';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://example.com';

const sampleReportsSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebPage',
  '@id': `${siteUrl}/sample-reports`,
  url: `${siteUrl}/sample-reports`,
  name: 'Sample Reports – PowerLottery.co',
  description:
    'View sample outputs from the PowerLottery.co analytics engine. See examples of POWERBALL and MegaMillions analysis reports including hot numbers, cold numbers, and trend data.',
  isPartOf: { '@id': `${siteUrl}/#website` },
};

const reportData = {
  megamillions: {
    label: 'MegaMillions',
    reportDate: 'March 28, 2025',
    drawsAnalyzed: 52,
    hotNumbers: [
      { ball: 14, freq: 18, pct: '+34%' },
      { ball: 31, freq: 17, pct: '+27%' },
      { ball: 7,  freq: 16, pct: '+20%' },
      { ball: 44, freq: 16, pct: '+20%' },
      { ball: 22, freq: 15, pct: '+12%' },
    ],
    coldNumbers: [
      { ball: 3,  freq: 5, pct: '-63%' },
      { ball: 55, freq: 6, pct: '-55%' },
      { ball: 38, freq: 7, pct: '-48%' },
      { ball: 61, freq: 7, pct: '-48%' },
      { ball: 19, freq: 8, pct: '-40%' },
    ],
    trendingUp: [
      { ball: 27, recentFreq: 6, baseFreq: 2, momentum: 'Strong' },
      { ball: 9,  recentFreq: 5, baseFreq: 2, momentum: 'Strong' },
      { ball: 50, recentFreq: 5, baseFreq: 3, momentum: 'Moderate' },
    ],
    trendingDown: [
      { ball: 14, recentFreq: 2, baseFreq: 8, momentum: 'Strong' },
      { ball: 36, recentFreq: 3, baseFreq: 7, momentum: 'Moderate' },
    ],
    megaBall: {
      hot: [{ ball: 15, freq: 9 }, { ball: 3, freq: 8 }, { ball: 22, freq: 7 }],
      cold: [{ ball: 10, freq: 1 }, { ball: 25, freq: 2 }],
    },
    anomalies: [
      'Ball 27 appeared in 4 of the last 6 draws — well above its 52-draw baseline of 0.8 per 6 draws.',
      'Even numbers accounted for 72% of all drawn balls over the last 12 draws, vs. a 50% long-run baseline.',
      'Numbers 40–50 have been absent from the top two positions for 9 consecutive draws.',
    ],
  },
  powerball: {
    label: 'PowerBall',
    reportDate: 'March 29, 2025',
    drawsAnalyzed: 52,
    hotNumbers: [
      { ball: 32, freq: 19, pct: '+42%' },
      { ball: 11, freq: 17, pct: '+28%' },
      { ball: 47, freq: 16, pct: '+20%' },
      { ball: 23, freq: 15, pct: '+13%' },
      { ball: 5,  freq: 15, pct: '+13%' },
    ],
    coldNumbers: [
      { ball: 60, freq: 4,  pct: '-70%' },
      { ball: 42, freq: 6,  pct: '-55%' },
      { ball: 17, freq: 7,  pct: '-47%' },
      { ball: 34, freq: 8,  pct: '-40%' },
      { ball: 26, freq: 8,  pct: '-40%' },
    ],
    trendingUp: [
      { ball: 11, recentFreq: 7, baseFreq: 2, momentum: 'Strong' },
      { ball: 55, recentFreq: 5, baseFreq: 2, momentum: 'Strong' },
      { ball: 8,  recentFreq: 4, baseFreq: 2, momentum: 'Moderate' },
    ],
    trendingDown: [
      { ball: 32, recentFreq: 2, baseFreq: 9, momentum: 'Strong' },
      { ball: 48, recentFreq: 3, baseFreq: 6, momentum: 'Moderate' },
    ],
    megaBall: {
      hot: [{ ball: 6, freq: 10 }, { ball: 20, freq: 8 }, { ball: 14, freq: 7 }],
      cold: [{ ball: 24, freq: 1 }, { ball: 11, freq: 2 }],
    },
    anomalies: [
      'Ball 32 appeared in 5 of the last 7 draws — nearly 3× its expected 52-draw rate.',
      'Consecutive number pairs (e.g., 10-11, 22-23) appeared in 38% of draws over the last 16 — vs. 18% baseline.',
      'The Powerball red ball value has been ≤ 10 in 8 of the last 10 draws (long-run expectation: 4 of 10).',
    ],
  },
};

const momentumColor: Record<string, string> = {
  Strong: 'text-[#FF69B4] font-semibold',
  Moderate: 'text-gray-600',
};

export default function SampleReportsPage() {
  const [active, setActive] = useState<Lottery>('megamillions');
  const data = reportData[active];

  return (
    <>
      <title>Sample Reports – PowerLottery.co POWERBALL &amp; MegaMillions Analytics</title>
      <meta name="description" content="See sample outputs from the PowerLottery.co analytics engine. Real-time POWERBALL and MegaMillions reports with hot numbers, cold numbers, and trend analysis, generated fresh on each request." />
      <meta name="keywords" content="lottery sample report, powerball analytics example, megamillions number analysis, lottery trend report" />
      <link rel="canonical" href={`${siteUrl}/sample-reports`} />
      <meta property="og:url" content={`${siteUrl}/sample-reports`} />
      <meta property="og:title" content="Sample Reports – PowerLottery.co POWERBALL &amp; MegaMillions Analytics" />
      <meta property="og:description" content="See sample outputs from the PowerLottery.co analytics engine — hot numbers, cold numbers, and trend analysis for POWERBALL and MegaMillions." />
      <meta name="twitter:title" content="Sample Reports – PowerLottery.co POWERBALL &amp; MegaMillions Analytics" />
      <meta name="twitter:description" content="See sample outputs from the PowerLottery.co analytics engine — hot numbers, cold numbers, and trend analysis for POWERBALL and MegaMillions." />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(sampleReportsSchema) }}
      />
      <div className="min-h-screen bg-white font-['Inter',sans-serif]">
        {/* Header */}
        <header className="bg-black py-12 text-center border-b border-gray-100">
          <div className="flex justify-center items-center mb-6">
            <img
              src="https://public.readdy.ai/ai/img_res/a5d5b9d0-9163-482d-be03-b47dc644bd65.png"
              alt="PowerLottery.co Logo"
              className="w-16 h-16 mr-4 object-contain"
            />
            <h1 className="text-5xl font-bold">
              <Link to="/" className="text-white bg-black px-2 py-1 rounded-lg mr-1">Power</Link>
              <span className="text-[#FF69B4]">Lottery.co</span>
            </h1>
          </div>
          <p className="text-2xl text-white font-medium">Smarter Lottery Players</p>
        </header>

        {/* Nav breadcrumb */}
        <div className="bg-gray-50 border-b border-gray-200">
          <div className="max-w-5xl mx-auto px-6 py-3 flex items-center space-x-2 text-sm text-gray-500">
            <Link to="/" className="hover:text-[#FF69B4] transition-colors cursor-pointer">Home</Link>
            <span>/</span>
            <span className="text-gray-800 font-medium">Sample Reports</span>
          </div>
        </div>

        <main className="max-w-5xl mx-auto px-6 py-16">
          {/* Page title */}
          <div className="mb-10">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Sample Reports</h2>
            <p className="text-gray-500 text-lg">
              Sample outputs from the PowerLottery.co analytics engine can be found on our{' '}
              <a
                href="https://www.facebook.com/profile.php?id=61587303745027"
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="text-[#FF69B4] hover:underline cursor-pointer font-medium"
              >
                FaceBook page
              </a>.
              <br />
              Actual reports are generated fresh on each request using the latest drawing data.
            </p>
          </div>









          {/* Disclaimer + CTA */}
          <div className="bg-black rounded-xl p-10 text-center">
            <p className="text-gray-400 text-sm mb-6 max-w-xl mx-auto">
              This is a sample report for illustration purposes. Actual reports are generated live from official drawing data at the time of your request.
            </p>
            <Link
              to="/"
              className="inline-block bg-[#FF69B4] text-white font-semibold px-10 py-3 rounded-lg hover:bg-[#FF1493] transition-colors whitespace-nowrap cursor-pointer"
            >
              Get Your License Key
            </Link>
          </div>
        </main>

        {/* Footer */}
        <footer className="bg-black py-12 text-center border-t border-gray-700">
          <div className="max-w-4xl mx-auto px-6">
            <p className="text-white text-lg mb-4">
              The lottery is a game of chance. Winning is not guaranteed. Play responsibly.
            </p>
            <div className="flex justify-center items-center space-x-2 text-white">
              <span>©</span>
              <span className="font-medium">PowerLottery.co – All Rights Reserved.</span>
            </div>
            <div className="mt-4">
              <a
                href="https://readdy.ai/?ref=logo"
                className="text-gray-300 text-sm hover:text-white transition-colors cursor-pointer"
              >
                Website Builder
              </a>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
}
